import { reactive, toRefs } from "vue";

/**
 * Versão do front publicada x versão carregada no navegador.
 *
 * O deploy do front (tag -> cd:tag) troca os arquivos e limpa o cache do Front Door, mas não
 * alcança as abas já abertas: elas seguem com o bundle antigo até o usuário recarregar. Em
 * 16/10/2026 um cliente ficou sem o lápis de editar porque a aba dele ainda era anterior à 3.5.0,
 * que não sabia exibir a edição bloqueada que a API 4.7.0 passou a mandar.
 *
 * A versão do bundle vem do build (VUE_APP_VERSAO = tag, no compile-tag do CI) e a publicada, do
 * versao.txt que o deploy grava na raiz. Sem VUE_APP_VERSAO (servidor local) nada é verificado.
 * A recarga automática só acontece em momento seguro: na troca de tela (vira um carregamento
 * completo do destino) ou ao voltar para a aba sem nenhum modal aberto; fora disso aparece o aviso
 * com o botão "Recarregar" (ver AvisoNovaVersao.vue).
 */
const VERSAO_CARREGADA = (process.env.VUE_APP_VERSAO ?? "").trim();
const INTERVALO_VERIFICACAO_MS = 5 * 60 * 1000;

const state = reactive({
  novaVersao: null as string | null
});

let iniciado = false;

const isVerificacaoAtiva = () => !!VERSAO_CARREGADA;

const verificarVersao = async (): Promise<string | null> => {
  if (!isVerificacaoAtiva()) {
    return null;
  }
  try {
    const resposta = await fetch("/versao.txt?nc=" + Date.now(), {
      cache: "no-store"
    });
    if (!resposta.ok) {
      return null;
    }
    const publicada = (await resposta.text()).trim();
    // resposta que não parece uma versão (página de erro, html) é ignorada
    if (publicada && publicada.length <= 40 && !publicada.includes("<")) {
      if (publicada !== VERSAO_CARREGADA) {
        state.novaVersao = publicada;
      }
    }
  } catch {
    // sem rede ou bloqueado: tenta de novo na próxima rodada
  }
  return state.novaVersao;
};

const isModalAberto = () =>
  !!document.querySelector(".p-dialog-mask, .p-dialog");

const recarregar = (destino?: string) => {
  if (destino) {
    window.location.href = destino;
  } else {
    window.location.reload();
  }
};

const iniciarVerificacaoVersao = () => {
  if (iniciado || !isVerificacaoAtiva()) {
    return;
  }
  iniciado = true;
  verificarVersao();
  window.setInterval(verificarVersao, INTERVALO_VERIFICACAO_MS);
  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    const nova = await verificarVersao();
    // de volta para a aba, sem formulário aberto em modal: recarrega já
    if (nova && !isModalAberto()) {
      recarregar();
    }
  });
};

export const useVersao = () => {
  return {
    iniciarVerificacaoVersao,
    verificarVersao,
    recarregar,
    versaoCarregada: VERSAO_CARREGADA,
    ...toRefs(state)
  };
};
